import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Typography, Spinner } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import {
    faList,
    faInbox,
    faExclamationTriangle,
    faUpload,
    faDownload,
    faArrowLeft,
    faCheckCircle,
    faSearch,
    faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { formatMoney } from '../shared/formatMoney';
import ConfirmModal from '../../common/ConfirmModal';
import { rootUrl } from '../../../config/config';

const APP_FONT_FAMILY = "'DM Sans', sans-serif";

const formatDate = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const VALID_TABS = ['all', 'unposted', 'unrecognised'];
const LIMIT = 50;

const BALANCE_TYPE_BADGE = {
    BUSINESS: 'bg-blue-100 text-blue-700',
    PERSONAL: 'bg-purple-100 text-purple-700',
};

const SOURCE_BADGE = {
    car_trans:   { label: 'CT',  cls: 'bg-green-100 text-green-700' },
    expense:     { label: 'EXP', cls: 'bg-yellow-100 text-yellow-700' },
    driver_trans:{ label: 'DT',  cls: 'bg-sky-100 text-sky-700' },
    cashbook:    { label: 'CB',  cls: 'bg-slate-100 text-slate-600' },
};

const TRANSFER_DIRECTION_OPTIONS = [
    { value: 'BUSINESS_TO_PERSONAL', label: 'Business → Personal' },
    { value: 'PERSONAL_TO_BUSINESS', label: 'Personal → Business' },
];

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: '12px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#FF5733' : '#d8dee6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
    menu: (base) => ({
        ...base,
        zIndex: 60,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff4ef' : 'white',
        color: state.isSelected ? 'white' : '#334155',
    }),
};

const MpesaStatement = () => {
    const { accountId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';
    const fileInputRef = useRef(null);

    const [account, setAccount] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [balanceTypeFilter, setBalanceTypeFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'all';
    const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
    const offset = (page - 1) * LIMIT;
    const [codeLookupInput, setCodeLookupInput] = useState('');
    const [codeLookupLoading, setCodeLookupLoading] = useState(false);

    // Import dialog state
    const [showImport, setShowImport] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [importResult, setImportResult] = useState(null);
    const [importPolling, setImportPolling] = useState(false);

    // Post confirm modal state
    const [confirmPost, setConfirmPost] = useState({ open: false, row: null });
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState('');

    // Internal transfer state
    const [confirmTransferPost, setConfirmTransferPost] = useState({ open: false, row: null });
    const [transferPosting, setTransferPosting] = useState(false);
    const [transferPostError, setTransferPostError] = useState('');
    const [transferForm, setTransferForm] = useState({
        transfer_direction: 'BUSINESS_TO_PERSONAL',
        entry_date: '',
        amount: '',
        description: '',
    });

    // Checkbox selection state (unposted tab)
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Multi-source check state
    const [checkingCarTrans, setCheckingCarTrans] = useState(false);
    const [checkResult, setCheckResult] = useState(null);

    // Bulk post confirm modal state
    const [confirmBulk, setConfirmBulk] = useState(false);
    const [bulkPosting, setBulkPosting] = useState(false);
    const [bulkError, setBulkError] = useState('');

    const [exporting, setExporting] = useState(false);

    const headers = { Authorization: `Bearer ${authToken}` };
    const resetPage = useCallback(() =>
        setSearchParams(prev => ({ tab: prev.get('tab') || 'all', page: '1' })),
    [setSearchParams]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchAccount = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/cashbook/accounts/${accountId}`, { headers });
            if (res.data?.success !== false) {
                setAccount(res.data?.data || res.data);
            }
        } catch (e) {
            setError('Failed to load account.');
        }
    }, [accountId]);

    useEffect(() => {
        fetchAccount();
    }, [fetchAccount]);

    // Debounce the search input → committed search value; reset page only on actual user input
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const t = setTimeout(() => {
            const trimmed = searchInput.trim();
            setSearch(trimmed);
            if (searchInput !== '') {
                setSearchParams(prev => ({ tab: prev.get('tab') || 'all', page: '1' }));
                setSelectedIds(new Set());
                setCheckResult(null);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchRows = useCallback(async () => {
        if (!account?.mpesa_phone) return;
        setLoading(true);
        setError('');
        try {
            const params = {
                mpesa_account: account.mpesa_phone,
                limit: LIMIT,
                offset,
                ...(dateFrom && { date_from: dateFrom }),
                ...(dateTo && { date_to: dateTo + 'T23:59:59' }),
                ...(balanceTypeFilter && { balance_type: balanceTypeFilter }),
                ...(search && { search }),
                ...(activeTab === 'unposted' && { posted: false, is_charge: false }),
                ...(activeTab === 'unrecognised' && { balance_type: 'null' }),
            };
            const res = await axios.get(`${rootUrl}/api/mpesa-statement`, { headers, params });
            if (res.data?.success) {
                setRows(res.data.data || []);
                setTotal(res.data.total || 0);
            } else {
                setError(res.data?.message || 'Failed to load statement.');
            }
        } catch (e) {
            setError('Failed to load statement rows.');
        } finally {
            setLoading(false);
        }
    }, [account, activeTab, dateFrom, dateTo, balanceTypeFilter, search, page]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (account) fetchRows();
    }, [fetchRows]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!account?.mpesa_phone) {
            setImportError('This account does not have an M-Pesa phone number set.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setImportError('');
        setImportResult(null);
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('mpesa_account', account.mpesa_phone);
            formData.append('file', file);

            const res = await axios.post(
                `${rootUrl}/api/mpesa-statement/import-file`,
                formData,
                {
                    headers: {
                        ...headers,
                    },
                }
            );
            if (res.data?.success) {
                setImportResult(res.data.data);
            } else {
                setImportError(res.data?.message || 'Import failed.');
            }
        } catch (e) {
            setImportError(e.response?.data?.message || 'Import request failed.');
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!importResult?.import_id) {
            setImportPolling(false);
            return undefined;
        }

        if (importResult.status === 'completed' || importResult.status === 'failed') {
            setImportPolling(false);
            return undefined;
        }

        setImportPolling(true);
        const timer = setInterval(async () => {
            try {
                const res = await axios.get(
                    `${rootUrl}/api/mpesa-statement/imports/${importResult.import_id}`,
                    { headers }
                );

                if (res.data?.success && res.data.data) {
                    const job = res.data.data;
                    setImportResult(job);

                    if (job.status === 'completed') {
                        setImportPolling(false);
                        fetchRows();
                    }

                    if (job.status === 'failed') {
                        setImportPolling(false);
                        setImportError(job.error_message || 'Import failed.');
                    }
                }
            } catch (e) {
                setImportPolling(false);
                setImportError(e.response?.data?.message || 'Failed to check import status.');
            }
        }, 3000);

        return () => clearInterval(timer);
    }, [importResult?.import_id, importResult?.status, fetchRows]);

    const handleMarkPosted = async () => {
        if (!confirmPost.row) return;
        setPosting(true);
        setPostError('');
        try {
            const res = await axios.patch(
                `${rootUrl}/api/mpesa-statement/${confirmPost.row.id}/post`,
                {},
                { headers }
            );
            if (res.data?.success) {
                setConfirmPost({ open: false, row: null });
                fetchRows();
            } else {
                setPostError(res.data?.message || 'Failed to mark as posted.');
            }
        } catch (e) {
            setPostError(e.response?.data?.message || 'Request failed.');
        } finally {
            setPosting(false);
        }
    };

    const getTransferPostDefaults = useCallback((row) => {
        const completionDate = row?.completion_time ? String(row.completion_time).slice(0, 10) : new Date().toISOString().slice(0, 10);
        const amount = row?.withdrawn || row?.paid_in || '';

        return {
            transfer_direction: 'BUSINESS_TO_PERSONAL',
            entry_date: completionDate,
            amount,
            description: row?.details || '',
        };
    }, []);

    const openInternalTransferPost = useCallback((row) => {
        setTransferPostError('');
        setTransferForm(getTransferPostDefaults(row));
        setConfirmTransferPost({ open: true, row });
    }, [getTransferPostDefaults]);

    const handlePostInternalTransfer = async () => {
        if (!confirmTransferPost.row) return;

        const amountValue = Number.parseFloat(transferForm.amount);
        if (!transferForm.transfer_direction || !transferForm.entry_date || Number.isNaN(amountValue) || amountValue <= 0) {
            setTransferPostError('Choose a transfer direction, date, and a valid positive amount.');
            return;
        }

        setTransferPosting(true);
        setTransferPostError('');
        try {
            const res = await axios.post(
                `${rootUrl}/api/mpesa-statement/${confirmTransferPost.row.id}/post-internal-transfer`,
                {
                    transfer_direction: transferForm.transfer_direction,
                    direction: transferForm.transfer_direction,
                    entry_date: transferForm.entry_date,
                    amount: amountValue,
                    description: transferForm.description,
                },
                { headers }
            );

            if (res.data?.success) {
                const postedRowId = confirmTransferPost.row.id;
                const updatedStatement = res.data.data?.statement || {
                    ...confirmTransferPost.row,
                    posted: true,
                    posting_method: 'MANUAL',
                    cashbook_entry_id: res.data.data?.cashbook_transfer?.source_entry?.id || confirmTransferPost.row.cashbook_entry_id || null,
                    transfer_group_id: res.data.data?.cashbook_transfer?.transfer_group_id || confirmTransferPost.row.transfer_group_id || null,
                    transfer_direction: res.data.data?.cashbook_transfer?.transfer_direction || transferForm.transfer_direction,
                    transfer_from_cashbook_account_id: res.data.data?.cashbook_transfer?.source_account?.id || confirmTransferPost.row.transfer_from_cashbook_account_id || null,
                    transfer_to_cashbook_account_id: res.data.data?.cashbook_transfer?.target_account?.id || confirmTransferPost.row.transfer_to_cashbook_account_id || null,
                    transfer_out_entry_id: res.data.data?.cashbook_transfer?.source_entry?.id || confirmTransferPost.row.transfer_out_entry_id || null,
                    transfer_in_entry_id: res.data.data?.cashbook_transfer?.target_entry?.id || confirmTransferPost.row.transfer_in_entry_id || null,
                };
                setConfirmTransferPost({ open: false, row: null });
                setTransferForm({
                    transfer_direction: 'BUSINESS_TO_PERSONAL',
                    entry_date: '',
                    amount: '',
                    description: '',
                });
                setSelectedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(postedRowId);
                    return next;
                });

                setRows((prevRows) => prevRows.map((item) => (
                    item.id === updatedStatement.id
                        ? { ...item, ...updatedStatement }
                        : item
                )));
            } else {
                setTransferPostError(res.data?.message || 'Failed to post internal transfer.');
            }
        } catch (e) {
            setTransferPostError(e.response?.data?.message || 'Request failed.');
        } finally {
            setTransferPosting(false);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === rows.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(rows.map(r => r.id)));
        }
    };

    const handleCheckCarTrans = async () => {
        setCheckingCarTrans(true);
        setCheckResult(null);
        try {
            const ids = selectedIds.size > 0 ? Array.from(selectedIds) : [];
            const res = await axios.post(
                `${rootUrl}/api/mpesa-statement/check-multi`,
                { mpesa_account: account.mpesa_phone, ids },
                { headers }
            );
            if (res.data?.success) {
                setCheckResult(res.data.data);
                setSelectedIds(new Set());
                fetchRows();
            } else {
                setCheckResult({ error: res.data?.message || 'Check failed.' });
            }
        } catch (e) {
            setCheckResult({ error: e.response?.data?.message || 'Request failed.' });
        } finally {
            setCheckingCarTrans(false);
        }
    };

    const handleFindByCode = async (event) => {
        event?.preventDefault();

        const code = String(codeLookupInput || '').trim().toUpperCase();
        if (!code) {
            setError('Enter an M-Pesa transaction code to search.');
            return;
        }

        if (!account?.mpesa_phone) {
            setError('This account does not have an M-Pesa phone number set.');
            return;
        }

        setCodeLookupLoading(true);
        setError('');
        try {
            const params = {
                mpesa_account: account.mpesa_phone,
                code,
            };
            const res = await axios.get(`${rootUrl}/api/mpesa-statement/find-by-code`, { headers, params });
            if (res.data?.success) {
                const matchedRows = res.data.data || [];
                setSearchParams({ tab: 'all', page: '1' });
                setDateFrom('');
                setDateTo('');
                setBalanceTypeFilter('');
                setSearchInput(code);
                setSearch(code);
                setSelectedIds(new Set());
                setCheckResult(null);
                setRows(matchedRows);
                setTotal(res.data.total || matchedRows.length);

                if (matchedRows.length === 0) {
                    setError(`No M-Pesa transaction found for code ${code}.`);
                }
            } else {
                setError(res.data?.message || 'Failed to find transaction by code.');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Request failed.');
        } finally {
            setCodeLookupLoading(false);
        }
    };

    const handleBulkPost = async () => {
        if (selectedIds.size === 0) return;
        setBulkPosting(true);
        setBulkError('');
        try {
            const ids = Array.from(selectedIds);
            const res = await axios.post(
                `${rootUrl}/api/mpesa-statement/bulk-post`,
                { ids },
                { headers }
            );
            if (res.data?.success) {
                setConfirmBulk(false);
                setSelectedIds(new Set());
                fetchRows();
            } else {
                setBulkError(res.data?.message || 'Bulk post failed.');
            }
        } catch (e) {
            setBulkError(e.response?.data?.message || 'Request failed.');
        } finally {
            setBulkPosting(false);
        }
    };

    const tabs = [
        { key: 'all', label: 'Transactions', icon: faList },
        { key: 'unposted', label: 'Unposted', icon: faInbox },
        { key: 'unrecognised', label: 'Unrecognised', icon: faExclamationTriangle },
    ];

    const handleExport = async () => {
        if (!account?.mpesa_phone) return;
        setExporting(true);
        try {
            const params = {
                mpesa_account: account.mpesa_phone,
                limit: 100000,
                offset: 0,
                ...(dateFrom && { date_from: dateFrom }),
                ...(dateTo && { date_to: dateTo + 'T23:59:59' }),
                ...(balanceTypeFilter && { balance_type: balanceTypeFilter }),
                ...(search && { search }),
                ...(activeTab === 'unposted' && { posted: false, is_charge: false }),
                ...(activeTab === 'unrecognised' && { balance_type: 'null' }),
            };
            const res = await axios.get(`${rootUrl}/api/mpesa-statement`, { headers, params });
            if (!res.data?.success) return;
            const allRows = res.data.data || [];
            const cols = [
                { key: 'completion_time', label: 'Date',         fmt: (v) => formatDate(v) },
                { key: 'receipt_no',      label: 'Receipt No.' },
                { key: 'details',         label: 'Details' },
                { key: 'paid_in',         label: 'Paid In' },
                { key: 'withdrawn',       label: 'Withdrawn' },
                { key: 'balance',         label: 'Balance' },
                { key: 'balance_type',    label: 'Wallet' },
                { key: 'posted',          label: 'Posted',       fmt: (v) => (v ? 'Yes' : 'No') },
                { key: 'matched_source',  label: 'Source' },
            ];
            const escape = (v) => {
                if (v == null) return '';
                const s = String(v);
                return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const header = cols.map((c) => c.label).join(',');
            const body = allRows.map((r) =>
                cols.map((c) => escape(c.fmt ? c.fmt(r[c.key]) : r[c.key])).join(',')
            ).join('\n');
            const csv = header + '\n' + body;
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const filename = [
                account.account_name?.replace(/\s+/g, '_') || 'mpesa',
                activeTab,
                dateFrom || '',
                dateTo   || '',
            ].filter(Boolean).join('_') + '.csv';
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            // silently ignore export errors — they don't affect the page state
        } finally {
            setExporting(false);
        }
    };

    const totalPages = Math.ceil(total / LIMIT);

    return (
        <div className="p-4 md:p-6" style={{ fontFamily: APP_FONT_FAMILY }}>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/cashbook')}
                        className="text-slate-500 hover:text-[#FF5733] transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <div>
                        <Typography className="text-xl font-bold text-slate-800">
                            M-Pesa Statement
                        </Typography>
                        {account && (
                            <Typography className="text-xs text-slate-500">
                                {account.account_name} &middot; {account.mpesa_phone}
                            </Typography>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <form onSubmit={handleFindByCode} className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2">
                        <FontAwesomeIcon icon={faSearch} className="text-slate-400 text-xs" />
                        <input
                            type="text"
                            placeholder="M-Pesa code"
                            value={codeLookupInput}
                            onChange={(e) => setCodeLookupInput(e.target.value.toUpperCase())}
                            className="w-32 bg-transparent outline-none text-xs text-slate-700 font-mono uppercase"
                        />
                        <button
                            type="submit"
                            disabled={codeLookupLoading}
                            className="text-xs font-semibold text-[#FF5733] hover:text-[#e04b2a] disabled:opacity-50"
                        >
                            {codeLookupLoading ? 'Finding…' : 'Find'}
                        </button>
                    </form>
                    {/* Export button */}
                    <button
                        onClick={handleExport}
                        disabled={exporting || !rows.length}
                        className="flex items-center gap-2 border border-slate-300 text-slate-700 text-xs font-semibold px-4 py-2 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        title="Export filtered records to CSV"
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        <span className="hidden sm:inline">{exporting ? 'Exporting…' : 'Export CSV'}</span>
                    </button>
                    {/* Import button */}
                    <button
                        onClick={() => { setShowImport(true); setImportResult(null); setImportError(''); }}
                        className="flex items-center gap-2 bg-[#FF5733] text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-[#e04b2a] transition-colors"
                    >
                        <FontAwesomeIcon icon={faUpload} />
                        <span className="hidden sm:inline">Import CSV</span>
                    </button>
                </div>
            </div>

            {/* No phone warning */}
            {account && !account.mpesa_phone && (
                <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                    This account has no M-Pesa phone number set. Edit the account in Cashbook to add one.
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 focus-within:border-[#FF5733] bg-white">
                    <FontAwesomeIcon icon={faSearch} className="text-slate-400 text-xs" />
                    <input
                        type="text"
                        placeholder="Search receipt or details…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="outline-none text-xs text-slate-700 w-48 bg-transparent"
                    />
                    {searchInput && (
                        <button onClick={() => { setSearchInput(''); setSearch(''); resetPage(); setSelectedIds(new Set()); setCheckResult(null); }} className="text-slate-400 hover:text-slate-600 text-xs leading-none">&times;</button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500">From</label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); resetPage(); setSelectedIds(new Set()); setCheckResult(null); }}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-[#FF5733]"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500">To</label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); resetPage(); setSelectedIds(new Set()); setCheckResult(null); }}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-[#FF5733]"
                    />
                </div>
                {activeTab === 'all' && (
                    <select
                        value={balanceTypeFilter}
                        onChange={(e) => { setBalanceTypeFilter(e.target.value); resetPage(); setSelectedIds(new Set()); setCheckResult(null); }}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-[#FF5733]"
                    >
                        <option value="">All Wallets</option>
                        <option value="BUSINESS">Business</option>
                        <option value="PERSONAL">Personal</option>
                    </select>
                )}
            </div>

            {/* Tab strip */}
            <div className="flex border-b border-gray-200 mb-5 overflow-x-auto">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => {
                            setSearchParams({ tab: tab.key, page: '1' });
                            setSelectedIds(new Set());
                            setCheckResult(null);
                        }}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                            activeTab === tab.key
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        <FontAwesomeIcon icon={tab.icon} className="md:hidden" />
                        <span className="hidden md:inline">{tab.label}</span>
                        <span className="md:hidden text-xs">{tab.label.split(' ')[0]}</span>
                    </button>
                ))}
            </div>

            {/* Error */}
            {error && (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</div>
            )}

            {/* Unposted toolbar */}
            {activeTab === 'unposted' && (
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    {selectedIds.size > 0 && (
                        <span className="text-xs text-slate-500 font-semibold">{selectedIds.size} selected</span>
                    )}
                    <button
                        onClick={handleCheckCarTrans}
                        disabled={checkingCarTrans}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faSearch} />
                        {checkingCarTrans ? 'Checking…' : (selectedIds.size > 0 ? `Check ${selectedIds.size} Selected` : 'Check All Sources')}
                    </button>
                    <button
                        onClick={() => { setBulkError(''); setConfirmBulk(true); }}
                        disabled={selectedIds.size === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#FF5733] text-xs font-semibold text-[#FF5733] hover:bg-orange-50 disabled:opacity-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faLayerGroup} />
                        {selectedIds.size > 0 ? `Mark ${selectedIds.size} Posted` : 'Mark Posted'}
                    </button>
                    {checkResult && !checkResult.error && (
                        <span className="text-xs text-emerald-600 font-semibold">
                            {checkResult.posted} of {checkResult.total_checked} posted
                            {checkResult.by_source && (
                                <> &mdash; {[
                                    checkResult.by_source.car_trans   > 0 && `${checkResult.by_source.car_trans} car_trans`,
                                    checkResult.by_source.expense     > 0 && `${checkResult.by_source.expense} expense`,
                                    checkResult.by_source.driver_trans > 0 && `${checkResult.by_source.driver_trans} driver_trans`,
                                    checkResult.by_source.cashbook    > 0 && `${checkResult.by_source.cashbook} cashbook`,
                                ].filter(Boolean).join(' · ')}</>
                            )}
                        </span>
                    )}
                    {checkResult?.error && (
                        <span className="text-xs text-red-500">{checkResult.error}</span>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-x-auto">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Spinner className="h-8 w-8 text-[#FF5733]" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="py-12 text-center text-sm text-slate-400">No statement rows found.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-100 bg-slate-50">
                                {activeTab === 'unposted' && (
                                    <th className="px-4 py-3 text-center w-8">
                                        <input
                                            type="checkbox"
                                            checked={rows.length > 0 && selectedIds.size === rows.length}
                                            onChange={toggleSelectAll}
                                            className="cursor-pointer accent-[#FF5733]"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Receipt No.</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Details</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Paid In</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Withdrawn</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Balance</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Wallet</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Status</th>
                                {activeTab === 'unposted' && (
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Action</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeTab === 'unposted' && selectedIds.has(row.id) ? 'bg-orange-50' : ''}`}>
                                    {activeTab === 'unposted' && (
                                        <td className="px-4 py-3 text-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                onChange={() => toggleSelect(row.id)}
                                                className="cursor-pointer accent-[#FF5733]"
                                            />
                                        </td>
                                    )}
                                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDate(row.completion_time)}</td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-700 whitespace-nowrap">{row.receipt_no}</td>
                                    <td className="px-4 py-3 text-xs text-slate-600 max-w-xs">
                                        <span className={row.is_charge ? 'text-slate-400 italic' : ''}>
                                            {row.details || '-'}
                                        </span>
                                        {row.is_charge && (
                                            <span className="ml-1 inline-block rounded px-1 py-0.5 text-[10px] bg-slate-100 text-slate-500">Charge</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-right text-emerald-600 whitespace-nowrap">
                                        {row.paid_in ? formatMoney(row.paid_in) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-right text-red-500 whitespace-nowrap">
                                        {row.withdrawn ? formatMoney(row.withdrawn) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-right font-semibold text-slate-700 whitespace-nowrap">
                                        {row.balance ? formatMoney(row.balance) : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {row.balance_type ? (
                                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${BALANCE_TYPE_BADGE[row.balance_type] || 'bg-gray-100 text-gray-500'}`}>
                                                {row.balance_type}
                                            </span>
                                        ) : (
                                            <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-600">?</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {row.posted ? (
                                            <div className="flex flex-col items-center gap-1">
                                                <span className="inline-flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} className="text-sm" title="Posted" />
                                                    {row.matched_source && SOURCE_BADGE[row.matched_source] && (
                                                        <span className={`inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold ${SOURCE_BADGE[row.matched_source].cls}`}
                                                            title={row.matched_source}>
                                                            {SOURCE_BADGE[row.matched_source].label}
                                                        </span>
                                                    )}
                                                </span>
                                                {(row.posting_method === 'MANUAL' || row.transfer_group_id || row.cashbook_entry_id || row.transfer_out_entry_id || row.transfer_in_entry_id) && (
                                                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                                                        {row.posting_method === 'MANUAL' && (
                                                            <span className="inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold bg-orange-100 text-orange-700">
                                                                MANUAL
                                                            </span>
                                                        )}
                                                        {row.transfer_group_id && (
                                                            <span className="inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold bg-violet-100 text-violet-700" title={row.transfer_group_id}>
                                                                TG #{String(row.transfer_group_id).slice(0, 8)}
                                                            </span>
                                                        )}
                                                        {row.cashbook_entry_id && (
                                                            <span className="text-[10px] text-slate-400">CB #{row.cashbook_entry_id}</span>
                                                        )}
                                                        {row.transfer_out_entry_id && (
                                                            <span className="text-[10px] text-slate-400">OUT #{row.transfer_out_entry_id}</span>
                                                        )}
                                                        {row.transfer_in_entry_id && (
                                                            <span className="text-[10px] text-slate-400">IN #{row.transfer_in_entry_id}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-[10px] text-slate-400">—</span>
                                        )}
                                    </td>
                                    {activeTab === 'unposted' && (
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <button
                                                    onClick={() => openInternalTransferPost(row)}
                                                    className="rounded-lg bg-[#FF5733] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#e04b2a] transition-colors"
                                                >
                                                    Transfer
                                                </button>
                                                <button
                                                    onClick={() => { setPostError(''); setConfirmPost({ open: true, row }); }}
                                                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 hover:underline"
                                                >
                                                    Mark Posted
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
                    <span>{total} total rows</span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setSearchParams({ tab: activeTab, page: String(page - 1) })}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                        >
                            Previous
                        </button>
                        <span>Page {page} / {totalPages}</span>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => setSearchParams({ tab: activeTab, page: String(page + 1) })}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Import CSV Dialog */}
            {showImport && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" style={{ fontFamily: APP_FONT_FAMILY }}>
                        <Typography className="text-lg font-bold text-slate-800 mb-1">Import M-Pesa Statement</Typography>
                        <Typography className="text-xs text-slate-500 mb-4">
                            Upload the original CSV file. The backend stores it temporarily, processes it in the queue, and refreshes the statement when the import finishes.
                        </Typography>

                        {importResult ? (
                            <div className={`rounded-xl border px-4 py-3 text-sm mb-4 ${
                                importResult.status === 'failed'
                                    ? 'bg-red-50 border-red-200 text-red-700'
                                    : importResult.status === 'completed'
                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                        : 'bg-slate-50 border-slate-200 text-slate-700'
                            }`}>
                                <p className="font-semibold capitalize">{importResult.status || 'queued'}</p>
                                {importResult.status === 'completed' ? (
                                    <p>{importResult.inserted_count ?? importResult.inserted ?? 0} rows inserted &middot; {importResult.skipped_count ?? importResult.skipped ?? 0} skipped</p>
                                ) : (
                                    <p>Import ID {importResult.id || importResult.import_id} is being processed in the background.</p>
                                )}
                                {importResult.error_message && (
                                    <p className="mt-1 text-xs">{importResult.error_message}</p>
                                )}
                            </div>
                        ) : (
                            <div className="mb-4">
                                <label
                                    htmlFor="mpesa-csv-upload"
                                    className="flex flex-col items-center justify-center gap-2 cursor-pointer rounded-xl border-2 border-dashed border-slate-300 hover:border-[#FF5733] py-8 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faUpload} className="text-2xl text-slate-400" />
                                    <span className="text-sm text-slate-500">Click to select CSV file</span>
                                    <span className="text-xs text-slate-400">.csv files only</span>
                                </label>
                                <input
                                    id="mpesa-csv-upload"
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                    disabled={importing}
                                />
                            </div>
                        )}

                        {importing && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                                <Spinner className="h-4 w-4" /> Importing...
                            </div>
                        )}

                        {importPolling && !importing && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                                <Spinner className="h-4 w-4" /> Processing in the background...
                            </div>
                        )}

                        {importError && (
                            <p className="text-xs text-red-500 mb-4">{importError}</p>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowImport(false)}
                                className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
                            >
                                {importResult ? 'Close' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mark Posted confirm (single row) */}
            <ConfirmModal
                isOpen={confirmPost.open}
                title="Mark as Posted"
                description={
                    confirmPost.row
                        ? `Mark receipt ${confirmPost.row.receipt_no} (${formatDate(confirmPost.row.completion_time)}) as posted?`
                        : ''
                }
                onCancel={() => setConfirmPost({ open: false, row: null })}
                onConfirm={handleMarkPosted}
                confirmText="Mark Posted"
                cancelText="Cancel"
                loading={posting}
                error={postError}
                variant="primary"
            />

            {/* Internal transfer modal */}
            {confirmTransferPost.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-slate-100" style={{ fontFamily: APP_FONT_FAMILY }}>
                        <div className="border-b border-slate-100 px-6 py-5">
                            <Typography className="text-xl font-bold text-slate-800">Record Internal Transfer</Typography>
                            <Typography className="text-sm text-slate-500 mt-1">
                                Record this imported M-Pesa row as a paired transfer between the linked personal and business wallets.
                            </Typography>
                        </div>

                        <div className="px-6 py-5 space-y-5 max-h-[72vh] overflow-y-auto">
                            {transferPostError && (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                                    {transferPostError}
                                </div>
                            )}

                            {confirmTransferPost.row && (
                                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                                    <div className="grid gap-3 md:grid-cols-2 text-sm">
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Receipt No.</div>
                                            <div className="font-semibold text-slate-700">{confirmTransferPost.row.receipt_no}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Completion Time</div>
                                            <div className="font-semibold text-slate-700">{formatDate(confirmTransferPost.row.completion_time)}</div>
                                        </div>
                                        <div className="md:col-span-2">
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Details</div>
                                            <div className="font-semibold text-slate-700">{confirmTransferPost.row.details || '-'}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Wallet Context</div>
                                            <div className="font-semibold text-slate-700">
                                                {account?.account_name || '-'}
                                                {account?.mpesa_role && (
                                                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                                                        {account.mpesa_role}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Default Direction</div>
                                            <div className="font-semibold text-slate-700">Business → Personal</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="md:col-span-2">
                                    <Typography className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Transfer Direction</Typography>
                                    <Select
                                        options={TRANSFER_DIRECTION_OPTIONS}
                                        value={TRANSFER_DIRECTION_OPTIONS.find((opt) => opt.value === transferForm.transfer_direction) || TRANSFER_DIRECTION_OPTIONS[0]}
                                        onChange={(opt) => setTransferForm((prev) => ({ ...prev, transfer_direction: opt?.value || 'BUSINESS_TO_PERSONAL' }))}
                                        styles={selectStyles}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isSearchable={false}
                                        placeholder="Choose transfer direction"
                                    />
                                    <p className="mt-2 text-[11px] text-slate-500">
                                        Business → Personal is the default. Choose Personal → Business only when money is moving back to the business wallet.
                                    </p>
                                </div>

                                <div>
                                    <Typography className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Amount (KES)</Typography>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={transferForm.amount}
                                        onChange={(e) => setTransferForm((prev) => ({ ...prev, amount: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733]"
                                    />
                                </div>

                                <div>
                                    <Typography className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Entry Date</Typography>
                                    <input
                                        type="date"
                                        value={transferForm.entry_date}
                                        onChange={(e) => setTransferForm((prev) => ({ ...prev, entry_date: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733]"
                                    />
                                </div>
                            </div>

                            <div>
                                <Typography className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Description / Memo</Typography>
                                <textarea
                                    rows={3}
                                    value={transferForm.description}
                                    onChange={(e) => setTransferForm((prev) => ({ ...prev, description: e.target.value }))}
                                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-all focus:border-[#FF5733] resize-none"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setConfirmTransferPost({ open: false, row: null });
                                    setTransferPostError('');
                                }}
                                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handlePostInternalTransfer}
                                disabled={transferPosting}
                                className="inline-flex items-center gap-2 rounded-xl bg-[#FF5733] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#e04b2a] disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
                            >
                                {transferPosting && <Spinner className="h-4 w-4" />}
                                {transferPosting ? 'Posting...' : 'Post Transfer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk post confirm */}
            <ConfirmModal
                isOpen={confirmBulk}
                title="Bulk Mark as Posted"
                description={`Mark ${selectedIds.size} selected row(s) as posted?`}
                onCancel={() => setConfirmBulk(false)}
                onConfirm={handleBulkPost}
                confirmText="Mark Posted"
                cancelText="Cancel"
                loading={bulkPosting}
                error={bulkError}
                variant="primary"
            />
        </div>
    );
};

export default MpesaStatement;
